import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './app/globals.css'
import { getValidatedConfig } from './lib/env-validation'
import { secureLogger } from './lib/secure-logger'
import { setupGlobalErrorHandlers, SecureErrorHandler } from './lib/secure-error-handler'
import { initSentry } from './lib/sentry'
import { initializeDatadog } from './lib/monitoring/datadog'
import { TRPCProvider } from './providers/TRPCProvider'

// Initialize monitoring before anything else
initSentry()
initializeDatadog()

// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Validate environment variables on startup
try {
  const config = getValidatedConfig()
} catch (error) {
  const secureError = SecureErrorHandler.handle(error, 'Environment validation')
  secureLogger.error('Application cannot start due to environment validation errors:', secureError)

  // Show user-friendly error in development
  if (import.meta.env.DEV) {
    document.body.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
        font-family: system-ui;
        background: #0f172a;
        color: white;
        padding: 2rem;
        text-align: center;
      ">
        <h1 style="color: #ef4444; margin-bottom: 1rem;">Environment Configuration Error</h1>
        <p style="margin-bottom: 2rem;">The application cannot start due to missing or invalid environment variables.</p>
        <div style="
          background: #1e293b;
          padding: 1rem;
          border-radius: 8px;
          font-family: monospace;
          text-align: left;
          max-width: 600px;
          white-space: pre-wrap;
        ">${String(error)}</div>
        <p style="margin-top: 2rem; color: #94a3b8;">Please check your .env file and restart the development server.</p>
      </div>
    `
  }
  throw error
}

// Setup global error handlers
setupGlobalErrorHandlers()

// Create a new router instance
const router = createRouter({ routeTree })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <TRPCProvider>
      <RouterProvider router={router} />
      {/* Show React Query Devtools in development */}
      {/* {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />} */}
    </TRPCProvider>
  </React.StrictMode>
)